import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Dropdown, Field, Input, Option, Spinner,
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Title3,
} from "@fluentui/react-components";
import { api } from "../../api/client";
import type { Priority } from "../../types/models";

interface Sla {
  id: string; name: string; priority: Priority;
  responseHours: number; resolutionHours: number; isActive: boolean;
}

const PRIORITIES: Priority[] = ["Low", "Medium", "High", "Critical"];

export function AdminSlasPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["slas"], queryFn: () => api.get<Sla[]>("/api/slas") });
  const [name, setName] = useState("");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [response, setResponse] = useState(4);
  const [resolution, setResolution] = useState(24);

  const create = useMutation({
    mutationFn: () => api.post("/api/slas", {
      name, priority, responseHours: response, resolutionHours: resolution,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["slas"] }); setName(""); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/slas/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["slas"] }),
  });

  return (
    <div>
      <Title3>SLAs</Title3>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
        <Field label="Name"><Input value={name} onChange={(_, d) => setName(d.value)} /></Field>
        <Field label="Priority">
          <Dropdown value={priority} selectedOptions={[priority]}
            onOptionSelect={(_, d) => setPriority((d.optionValue as Priority) ?? "Medium")}>
            {PRIORITIES.map((p) => <Option key={p} value={p}>{p}</Option>)}
          </Dropdown>
        </Field>
        <Field label="Response (hrs)">
          <Input type="number" value={String(response)} onChange={(_, d) => setResponse(Number(d.value))} />
        </Field>
        <Field label="Resolution (hrs)">
          <Input type="number" value={String(resolution)} onChange={(_, d) => setResolution(Number(d.value))} />
        </Field>
        <Button appearance="primary" disabled={!name} onClick={() => create.mutate()}>Add SLA</Button>
      </div>
      <div style={{ marginTop: 16 }}>
        {list.isLoading ? <Spinner /> : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Priority</TableHeaderCell>
                <TableHeaderCell>Response (hrs)</TableHeaderCell>
                <TableHeaderCell>Resolution (hrs)</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(list.data ?? []).map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.priority}</TableCell>
                  <TableCell>{s.responseHours}</TableCell>
                  <TableCell>{s.resolutionHours}</TableCell>
                  <TableCell><Button size="small" onClick={() => remove.mutate(s.id)}>Delete</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
